<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
 items: { type: Array, default: () => [] },
 dateField: { type: String, default: 'due_date' },
 labelField: { type: String, default: 'title' },
 sublabelField: { type: String, default: '' },
 idField: { type: String, default: 'id' },
 emptyLabel: { type: String, default: 'Nenhum item neste periodo' },
 itemType: { type: String, default: 'item' },
});

const emit = defineEmits(['item-click', 'day-click', 'month-change']);

const emitMonth = () =>
 emit('month-change', { year: currentYear.value, month: currentMonth.value });

const currentDate = ref(new Date());

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MONTHS = [
 'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const currentMonth = computed(() => currentDate.value.getMonth());
const currentYear = computed(() => currentDate.value.getFullYear());
const monthLabel = computed(() => `${MONTHS[currentMonth.value]} ${currentYear.value}`);

const todayDate = new Date();
const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;

const rowCount = computed(() => {
 const total = calendarDays.value.length;
 return total <= 35 ? 5 : 6;
});

const calendarDays = computed(() => {
 const year = currentYear.value;
 const month = currentMonth.value;
 const firstDay = new Date(year, month, 1);
 const lastDay = new Date(year, month + 1, 0);
 const startDow = firstDay.getDay();
 const totalDays = lastDay.getDate();
 const days = [];

 // Previous month padding
 const prevMonthLast = new Date(year, month, 0).getDate();
 for (let i = startDow - 1; i >= 0; i--) {
 const dayOfWeek = (startDow - 1 - i) % 7;
 days.push({
 date: prevMonthLast - i,
 isCurrentMonth: false,
 dateStr: formatDateStr(year, month - 1, prevMonthLast - i),
 isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
 });
 }

 // Current month days
 for (let d = 1; d <= totalDays; d++) {
 const dayOfWeek = new Date(year, month, d).getDay();
 days.push({
 date: d,
 isCurrentMonth: true,
 dateStr: formatDateStr(year, month, d),
 isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
 });
 }

 // Next month padding - fill to complete row (35 or 42)
 const totalNeeded = days.length <= 35 ? 35 : 42;
 const remaining = totalNeeded - days.length;
 for (let d = 1; d <= remaining; d++) {
 days.push({
 date: d,
 isCurrentMonth: false,
 dateStr: formatDateStr(year, month + 1, d),
 isWeekend: false,
 });
 }

 return days;
});

const itemsByDate = computed(() => {
 const map = {};
 props.items.forEach(item => {
 const dateVal = item[props.dateField];
 if (!dateVal) return;
 const dateStr = String(dateVal).split('T')[0];
 if (!map[dateStr]) map[dateStr] = [];
 map[dateStr].push(item);
 });
 return map;
});

const monthItemCount = computed(() => {
 const year = currentYear.value;
 const month = currentMonth.value;
 const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
 let count = 0;
 Object.keys(itemsByDate.value).forEach(dateStr => {
 if (dateStr.startsWith(prefix)) {
 count += itemsByDate.value[dateStr].length;
 }
 });
 return count;
});

const daysWithItems = computed(() => {
 const year = currentYear.value;
 const month = currentMonth.value;
 const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
 const uniqueDays = new Set();
 Object.keys(itemsByDate.value).forEach(dateStr => {
 if (dateStr.startsWith(prefix)) {
 uniqueDays.add(dateStr);
 }
 });
 return uniqueDays.size;
});

const formatDateStr = (year, month, day) => {
 const d = new Date(year, month, day);
 return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const prevMonth = () => {
 const d = new Date(currentDate.value);
 d.setMonth(d.getMonth() - 1);
 currentDate.value = d;
 emitMonth();
};

const nextMonth = () => {
 const d = new Date(currentDate.value);
 d.setMonth(d.getMonth() + 1);
 currentDate.value = d;
 emitMonth();
};

const goToday = () => {
 currentDate.value = new Date();
 emitMonth();
};

const onItemClick = (item, event) => {
 event.stopPropagation();
 emit('item-click', item);
};

const onDayClick = day => {
 if (day.isCurrentMonth) {
 emit('day-click', day.dateStr);
 }
};

const EVENT_COLORS = [
 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/20',
 'border-l-teal-500 bg-teal-50 dark:bg-teal-900/20',
 'border-l-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
 'border-l-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
 'border-l-pink-500 bg-pink-50 dark:bg-pink-900/20',
];

const getItemStyle = item => {
 const priority = item.priority;
 if (priority === 'urgent') return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
 if (priority === 'high') return 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20';
 if (priority === 'medium') return 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20';
 if (priority === 'low') return 'border-l-blue-400 bg-blue-50 dark:bg-blue-900/20';

 const status = item.status;
 if (status === 'completed' || status === 'resolved' || status === 'closed') return 'border-l-green-500 bg-green-50 dark:bg-green-900/20';
 if (status === 'in_progress' || status === 'open') return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20';

 // Use a deterministic color based on item id
 const id = item[props.idField] || 0;
 return EVENT_COLORS[id % EVENT_COLORS.length];
};

const isCompleted = item => {
 return ['completed', 'resolved', 'closed'].includes(item.status);
};

const maxVisibleItems = 3;
</script>

<template>
 <div class="flex flex-col h-full overflow-hidden">
 <!-- Calendar container with padding -->
 <div class="flex-1 flex flex-col overflow-hidden p-4 pb-2">
 <!-- Card wrapper -->
 <div class="flex-1 flex flex-col bg-n-solid-1 rounded-xl border border-n-weak shadow-sm overflow-hidden">
 <!-- Calendar header -->
 <div class="flex items-center justify-between px-4 py-2.5 border-b border-n-weak/80">
 <!-- Left: Navigation -->
 <div class="flex items-center gap-1">
 <button
 class="flex items-center justify-center size-7 rounded-md hover:bg-n-alpha-1 text-n-slate-9 transition-colors"
 @click="prevMonth"
 >
 <span class="i-lucide-chevron-left size-4" />
 </button>
 <h3 class="text-sm font-semibold text-n-slate-12 min-w-[160px] text-center select-none">
 {{ monthLabel }}
 </h3>
 <button
 class="flex items-center justify-center size-7 rounded-md hover:bg-n-alpha-1 text-n-slate-9 transition-colors"
 @click="nextMonth"
 >
 <span class="i-lucide-chevron-right size-4" />
 </button>
 </div>

 <!-- Right: Stats + Today -->
 <div class="flex items-center gap-3">
 <div class="hidden sm:flex items-center gap-4 text-[11px] text-n-slate-9">
 <span>
 <span class="font-semibold text-n-slate-11">{{ monthItemCount }}</span>
 {{ itemType }}(s)
 </span>
 <span>
 <span class="font-semibold text-n-slate-11">{{ daysWithItems }}</span>
 dia(s)
 </span>
 </div>
 <button
 class="text-[11px] font-medium px-2.5 py-1 rounded-md transition-colors"
 :class="
 todayStr.startsWith(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`)
 ? 'text-n-slate-9 cursor-default'
 : 'text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20'
 "
 @click="goToday"
 >
 Hoje
 </button>
 </div>
 </div>

 <!-- Weekday headers -->
 <div class="grid grid-cols-7 border-b border-n-weak/80">
 <div
 v-for="(day, idx) in WEEKDAYS"
 :key="day"
 class="py-2 text-[10px] font-semibold uppercase tracking-widest text-center"
 :class="idx === 0 || idx === 6 ? 'text-n-slate-7' : 'text-n-slate-9'"
 >
 {{ day }}
 </div>
 </div>

 <!-- Day cells grid -->
 <div class="flex-1 grid grid-cols-7 overflow-hidden" :style="{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }">
 <div
 v-for="(day, idx) in calendarDays"
 :key="idx"
 class="relative border-b border-r border-n-weak px-1 py-1 transition-colors cursor-pointer group/day overflow-hidden"
 :class="{
 'bg-n-slate-2/60 dark:bg-n-slate-12/50': !day.isCurrentMonth,
 'bg-n-solid-1': day.isCurrentMonth && !day.isWeekend && day.dateStr !== todayStr,
 'bg-n-slate-2': day.isCurrentMonth && day.isWeekend && day.dateStr !== todayStr,
 'bg-woot-50/40 dark:bg-woot-900/10': day.dateStr === todayStr,
 'border-r-transparent': (idx + 1) % 7 === 0,
 'border-b-transparent': idx >= calendarDays.length - 7,
 }"
 @click="onDayClick(day)"
 >
 <!-- Day number -->
 <div class="flex items-center justify-between mb-0.5 px-0.5">
 <span
 class="text-[11px] font-medium w-6 h-6 flex items-center justify-center rounded-full leading-none"
 :class="{
 'text-n-slate-7': !day.isCurrentMonth,
 'text-n-slate-10': day.isCurrentMonth && day.dateStr !== todayStr,
 'bg-woot-500 text-white font-bold': day.dateStr === todayStr,
 }"
 >
 {{ day.date }}
 </span>

 <!-- Hover add indicator -->
 <span
 v-if="day.isCurrentMonth"
 class="i-lucide-plus size-3.5 text-n-slate-7 opacity-0 group-hover/day:opacity-100 transition-opacity"
 />
 </div>

 <!-- Events list -->
 <div class="space-y-0.5">
 <button
 v-for="item in (itemsByDate[day.dateStr] || []).slice(0, maxVisibleItems)"
 :key="item[idField]"
 class="w-full flex items-center gap-1 pl-1.5 pr-1 py-[3px] rounded text-left transition-all text-[10px] leading-tight truncate"
 :class="[
 getItemStyle(item),
 isCompleted(item) ? 'opacity-50' : 'hover:opacity-80',
 ]"
 @click="onItemClick(item, $event)"
 >
 <span
  v-if="item.kind"
  class="shrink-0 size-3"
  :class="item.kind === 'meeting' ? 'i-lucide-calendar-clock' : 'i-lucide-list-todo'"
 />
 <span
 class="truncate flex-1 font-medium"
 :class="{
 'text-n-slate-11': !isCompleted(item),
 'text-n-slate-9 line-through': isCompleted(item),
 }"
 >
 {{ item[labelField] }}
 </span>
 </button>
 </div>

 <!-- Overflow -->
 <button
 v-if="(itemsByDate[day.dateStr] || []).length > maxVisibleItems"
 class="w-full text-[9px] font-semibold text-n-slate-9 hover:text-woot-500 dark:hover:text-woot-400 text-left px-1.5 pt-0.5 transition-colors"
 @click.stop
 >
 +{{ (itemsByDate[day.dateStr] || []).length - maxVisibleItems }} mais
 </button>
 </div>
 </div>
 </div>
 </div>

 <!-- Empty state (centered over calendar) -->
 <Teleport to="body">
 <div
 v-if="items.length === 0"
 class="fixed inset-0 flex items-center justify-center pointer-events-none z-10"
 style="top: 140px"
 >
 <div class="text-center pointer-events-none">
 <span class="i-lucide-calendar-x2 size-10 text-n-slate-5 dark:text-n-slate-11 block mx-auto mb-2" />
 <p class="text-xs text-n-slate-9">{{ emptyLabel }}</p>
 </div>
 </div>
 </Teleport>
 </div>
</template>
